import React from "react";
import {
  Stack,
  TextField,
  MenuItem,
  useTheme,
  Typography,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ENVIRONMENTS } from "../constants";

// Glassmorphic container for the step
const GlassStepContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.8
  )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.3
    )}, transparent)`,
    zIndex: 1,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, ${alpha(
      theme.palette.primary.main,
      0.02
    )} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${alpha(
      theme.palette.secondary.main,
      0.02
    )} 0%, transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },
  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

// Glassmorphic TextField with enhanced styling
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.7
    )} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
    backdropFilter: "blur(15px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    borderRadius: 12,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, transparent, ${alpha(
        theme.palette.primary.main,
        0.3
      )}, transparent)`,
      zIndex: 1,
    },

    "&:hover": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.background.paper,
        0.8
      )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
      transform: "translateY(-1px)",
      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
    },

    "&.Mui-focused": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.background.paper,
        0.85
      )} 0%, ${alpha(theme.palette.background.paper, 0.65)} 100%)`,
      boxShadow: `0 0 0 3px ${alpha(
        theme.palette.primary.main,
        0.1
      )}, 0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
      transform: "translateY(-2px)",
    },

    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "& .MuiInputBase-input": {
      position: "relative",
      zIndex: 2,
      color: theme.palette.text.primary,
      fontWeight: 500,
      "&::placeholder": {
        color: alpha(theme.palette.text.primary, 0.6),
        opacity: 1,
      },
    },
  },

  "& .MuiInputLabel-root": {
    color: alpha(theme.palette.text.primary, 0.8),
    fontWeight: 600,
    fontSize: "0.9rem",
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    transition: "all 0.3s ease",

    "&.Mui-focused": {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      transform: "scale(0.9)",
    },

    "&.MuiInputLabel-shrink": {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
  },

  "& .MuiFormHelperText-root": {
    color: alpha(theme.palette.text.secondary, 0.9),
    fontWeight: 500,
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.75),
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.info.main,
      0.08
    )} 0%, ${alpha(theme.palette.info.main, 0.04)} 100%)`,
    backdropFilter: "blur(8px)",
    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
    borderRadius: 6,
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.5, 0, 0, 0),
  },
}));

// Glassmorphic MenuItem with hover effects
const GlassMenuItem = styled(MenuItem)(({ theme }) => ({
  background: "transparent",
  transition: "all 0.2s ease",
  borderRadius: 8,
  margin: theme.spacing(0.25, 0.5),

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.12
    )} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    transform: "translateX(4px)",
  },

  "&.Mui-selected": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.18
    )} 0%, ${alpha(theme.palette.primary.main, 0.09)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    color: theme.palette.primary.main,
    fontWeight: 600,

    "&:hover": {
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.22
      )} 0%, ${alpha(theme.palette.primary.main, 0.11)} 100%)`,
    },
  },
}));

const BasicInfoStep = ({ formState, handleInputChange }) => {
  const theme = useTheme();

  return (
    <GlassStepContainer spacing={3}>
      <GlassTextField
        label="Divesite Name"
        name="name"
        value={formState.name}
        onChange={handleInputChange}
        fullWidth
        required
        placeholder="Enter the name of the dive site"
      />

      <GlassTextField
        label="Diving location at divesite (ex. Main Reef)"
        name="location"
        value={formState.location}
        onChange={handleInputChange}
        fullWidth
        required
        placeholder="Describe the specific diving location"
      />

      <GlassTextField
        select
        label="Dive type"
        name="environment"
        value={formState.environment}
        onChange={handleInputChange}
        helperText="Please select dive type."
        fullWidth
        required
        SelectProps={{
          MenuProps: {
            PaperProps: {
              sx: {
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.paper,
                  0.95
                )} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
                backdropFilter: "blur(20px)",
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                borderRadius: 2,
                boxShadow: `0 8px 32px ${alpha(
                  theme.palette.common.black,
                  0.15
                )}`,
                "& .MuiList-root": {
                  padding: theme.spacing(0.5),
                },
              },
            },
          },
        }}
      >
        {ENVIRONMENTS.map((option) => (
          <GlassMenuItem key={option.value} value={option.value}>
            {option.label}
          </GlassMenuItem>
        ))}
      </GlassTextField>
    </GlassStepContainer>
  );
};

export default BasicInfoStep;
